<script setup lang="ts">
import { ref, computed } from "vue";
import Button from "primevue/button";
import { Participant, Project, Trainer } from "../../../types/PersologOnlineTypes.ts";
import { ProfileAdministrationAPI } from "../../../api";
import { useI18n } from "vue-i18n";
import { processInBatches } from "../../../utils/GeneralUtils.ts";
import { sendReminderToParticipant, sendActivationEmail } from "../../../utils/buttons-logic/sendQuestionnaireLogic.ts";
import { useToast } from "primevue/usetoast";

const { t } = useI18n();

const props = defineProps({
    productName: {
        type: String,
        required: true,
    },
    project: {
        type: Object as () => Project,
        required: true,
    },
    participants: {
        type: Array as () => Participant[],
        required: true,
    },
    trainer: {
        type: Object as () => Trainer,
        required: true,
    },
    quantity: {
        type: Number,
        required: true,
    },
    withLabel: {
        type: Boolean,
        default: false,
    },
    withClasses: {
        type: String,
        default: "",
    },
});

const emit = defineEmits(["update:participants"]);
const loading = ref(false);
const toast = useToast();

const hasParticipantsToActivate = computed(() => props.participants.some((p) => p.status === "CREATED"));

const hasParticipantsToRemind = computed(() =>
    props.participants.some((p) => ["ACTIVATED", "STARTED"].includes(p.status!)),
);

async function activateParticipants() {
    loading.value = true;
    try {
        const participantsToActivate = props.participants.filter((p) => p.status === "CREATED");
        const activatedParticipants = await ProfileAdministrationAPI.activateParticipants(props.project?.id!!);
        const participantsToCreate = activatedParticipants.slice(0, props.quantity);

        const updatedParticipants = await processInBatches(participantsToCreate, async (p) => {
            await sendActivationEmail(props.trainer, p, props.project);
            return p;
        });

        emit("update:participants", updatedParticipants);
        if (activatedParticipants.length < participantsToActivate.length) {
            showWarnToast(activatedParticipants.length, participantsToActivate.length);
        } else {
            showSuccessToast();
        }
    } catch (error) {
        console.error(error);
    } finally {
        loading.value = false;
    }
}

function showSuccessToast() {
    toast.add({
        severity: "success",
        summary: t("project-details.participants.toasts.email.summary"),
        detail: t("project-details.participants.toasts.email.success"),
        life: 5000,
    });
}

function showWarnToast(activated: number, total: number) {
    toast.add({
        severity: "warn",
        summary: t("project-details.participants.toasts.email.summary"),
        detail: t("project-details.participants.toasts.email.warn", { activated, total, missing: total - activated }),
    });
}

function showErrorToast() {
    toast.add({
        severity: "error",
        summary: t("project-details.participants.toasts.email.summary"),
        detail: t("project-details.participants.toasts.email.error"),
        life: 5000,
    });
}

async function remindParticipants() {
    loading.value = true;
    try {
        const participantsToRemind = props.participants.filter((p) => ["ACTIVATED", "STARTED"].includes(p.status!));
        await processInBatches(participantsToRemind, async (p) => {
            await sendReminderToParticipant(props.trainer, p, props.project);
            return p;
        });
        showSuccessToast();
    } catch (error) {
        console.error(error);
        showErrorToast();
    } finally {
        loading.value = false;
    }
}
</script>

<template>
    <Button
        :id="'send-or-remind-all_btn_send-' + project.id"
        :outlined="!withLabel"
        :class="withClasses"
        :label="withLabel ? t('project-details.participants.callForAction.activate') : ''"
        v-if="hasParticipantsToActivate && quantity > 0"
        :icon="loading ? 'pi pi-spin pi-spinner' : 'pi pi-send'"
        @click="activateParticipants"
        :disabled="loading"
        v-tooltip.top="{ value: t('project-details.participants.tooltips.activate'), showDelay: 500 }"
    />
    <Button
        :id="'send-or-remind-all_btn_remind-' + project.id"
        :outlined="!withLabel"
        :class="withClasses"
        :label="withLabel ? t('project-details.participants.callForAction.remind') : ''"
        v-if="hasParticipantsToRemind"
        :icon="loading ? 'pi pi-spin pi-spinner' : 'pi pi-bell'"
        @click="remindParticipants"
        :disabled="loading"
        v-tooltip.top="{ value: t('project-details.participants.tooltips.remind'), showDelay: 500 }"
    />
</template>
